import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Handshake, TrendingUp, Cloud, Shield, Award, CheckCircle } from 'lucide-react';

const PartnersPage = () => {
  const partners = [
    {
      name: "VantageMarkets",
      logo: "https://images.unsplash.com/photo-1560221328-12fe60f83ab8?w=400&h=200&fit=crop", // Placeholder - professional trading floor
      description: "Leading Global Trading Platform",
      story: "VantageMarkets is our premier financial services partner, providing cutting-edge trading infrastructure and market access to our clients. With over a decade of experience in global financial markets, VantageMarkets brings institutional-grade trading technology, deep liquidity, and competitive spreads across multiple asset classes.",
      services: [
        "Multi-asset trading platform access",
        "Institutional-grade execution",
        "Advanced charting and analysis tools",
        "24/7 market access and support",
        "Competitive spreads and low commissions",
        "Comprehensive risk management tools"
      ],
      website: "https://www.vantagemarkets.com",
      color: "from-blue-600 to-blue-800"
    },
    {
      name: "IOZoom",
      logo: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=200&fit=crop", // Placeholder - cloud infrastructure
      description: "Premium Cloud Infrastructure Provider",
      story: "IOZoom powers our entire digital infrastructure with their state-of-the-art cloud services. As a leading provider of high-performance cloud solutions, IOZoom ensures our platform maintains 99.9% uptime, lightning-fast performance, and enterprise-level security. Their scalable infrastructure allows us to grow seamlessly while maintaining the highest standards of reliability and data protection.",
      services: [
        "High-performance cloud servers",
        "99.9% uptime guarantee",
        "Enterprise-level security and DDoS protection",
        "Automated backups and disaster recovery",
        "24/7 technical support",
        "Scalable infrastructure solutions"
      ],
      website: "https://www.iozoom.com",
      color: "from-purple-600 to-purple-800"
    },
    {
      name: "Benchi.app",
      logo: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop", // Placeholder - analytics/dashboard
      description: "Advanced Analytics & Business Intelligence Platform",
      story: "Benchi.app is our strategic partner for cutting-edge analytics and business intelligence solutions. Their innovative platform empowers our clients with real-time insights, comprehensive data visualization, and predictive analytics capabilities. With Benchi.app's powerful tools, we deliver actionable intelligence that drives informed decision-making and accelerates business growth across all our investment portfolios.",
      services: [
        "Real-time analytics dashboards",
        "Custom data visualization tools",
        "Predictive analytics and forecasting",
        "Automated reporting and insights",
        "Multi-source data integration",
        "Advanced KPI tracking and monitoring"
      ],
      website: "https://www.benchi.app",
      color: "from-green-600 to-green-800"
    },
    {
      name: "Emergent.sh",
      logo: "https://images.pexels.com/photos/270623/pexels-photo-270623.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      description: "AI-Powered App Development Platform",
      story: "Emergent.sh is our technology partner revolutionizing how we build and deploy digital solutions. Backed by Y Combinator (S24), Emergent.sh enables the rapid creation of full-stack web and mobile applications using cutting-edge AI technology. Their platform empowers our team to deliver innovative client-facing tools, dashboards, and services at unprecedented speed — turning ideas into production-ready applications in minutes rather than months.",
      services: [
        "AI-powered full-stack app development",
        "Rapid prototyping and deployment",
        "Web and mobile application building",
        "Seamless third-party integrations",
        "Enterprise-grade infrastructure",
        "Y Combinator backed innovation"
      ],
      website: "https://emergent.sh",
      color: "from-orange-500 to-amber-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-4 bg-white/10 rounded-xl">
              <Handshake className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-white mb-2">
                Our Trusted Partners
              </h1>
              <p className="text-xl text-white/90">
                Building excellence through strategic partnerships
              </p>
            </div>
          </div>
          <p className="text-lg text-white/80 max-w-3xl">
            At FXC Management Services, we believe in the power of collaboration. Our carefully selected partners 
            share our commitment to excellence, innovation, and delivering exceptional value to our clients.
          </p>
        </div>
      </div>

      {/* Why Partnerships Matter */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Our Partnerships Matter
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We partner with industry leaders to ensure our clients receive world-class services, 
              cutting-edge technology, and unmatched reliability.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Excellence</h3>
              <p className="text-gray-600">
                We partner only with industry leaders who share our commitment to exceptional quality and service.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Security</h3>
              <p className="text-gray-600">
                Our partners provide enterprise-grade security and reliability, protecting your investments 24/7.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Innovation</h3>
              <p className="text-gray-600">
                Together, we leverage cutting-edge technology to deliver superior investment solutions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Partners Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-16">
            {partners.map((partner, index) => (
              <div 
                key={index}
                data-testid={`partner-card-${partner.name.toLowerCase().replace(/[\s.]/g, '-')}`}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
              >
                {/* Partner Header with Logo */}
                <div className={`bg-gradient-to-r ${partner.color} p-8`}>
                  <div className="flex items-center gap-6">
                    <div className="bg-white rounded-xl p-4 shadow-lg">
                      <img 
                        src={partner.logo} 
                        alt={`${partner.name} logo`}
                        className="w-32 h-16 object-cover rounded"
                      />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white mb-2">
                        {partner.name}
                      </h2>
                      <p className="text-xl text-white/90">
                        {partner.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  {/* Partner Story */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Handshake className="w-6 h-6 text-[#0d6571]" />
                      Our Partnership
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {partner.story}
                    </p>
                  </div>

                  {/* Services Grid */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-6 h-6 text-[#0d6571]" />
                      Key Services & Benefits
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {partner.services.map((service, idx) => (
                        <div 
                          key={idx}
                          className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-800">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Visit Website Button */}
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-[#0d6571] text-white rounded-lg hover:bg-[#0d6571]/90 font-semibold transition-colors"
                  >
                    Visit {partner.name} Website
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Become a Partner CTA */}
      <div className="bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Interested in Partnering With Us?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            We're always looking for innovative partners who share our vision of delivering 
            exceptional financial services and investment opportunities.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#0d6571] rounded-full hover:bg-gray-100 font-bold text-lg transition-colors"
          >
            Get in Touch
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PartnersPage;
